import { DependencyList, useEffect, useState } from 'react';

export interface IDisposable {
  dispose(): void;
}
export function useAsyncDisposable<AsyncDisposable extends IDisposable>(
  fn: () => Promise<AsyncDisposable | null>,
  deps: DependencyList = [],
) {
  const [workerState, setWorkerState] = useState<AsyncDisposable | null>(null);
  useEffect(() => {
    // Capture this run's promise locally. Each effect owns exactly the instance it
    // created — the cleanup below disposes *that* instance, not whatever a shared
    // mutable ref happens to point at when cleanup fires. This is what keeps the
    // published resource (workerState) and the surviving instance the same object
    // when the effect re-runs while a previous factory is still in flight (e.g. the
    // canvasReady flip or React StrictMode's double-invoke).
    let cancelled = false;
    const promise = fn();
    promise
      .then((worker) => {
        // If this run was superseded before its resource finished initializing, don't
        // publish it — the cleanup's own `promise.then` disposes it instead.
        if (worker && !cancelled) {
          setWorkerState(worker);
        }
      })
      .catch((error) => {
        console.error('Async disposable initialization failed:', error);
      });
    return () => {
      cancelled = true;
      promise.then((worker) => worker?.dispose()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return workerState;
}
